import React from "react";
import { Box, Button, Container, Stack, Typography, Paper, CircularProgress } from "@mui/material";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { loginRequest } from "./msalConfig";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { getPreferredAccount } from "./msalInstance";

const AuthLanding = () => {
  const { instance, accounts } = useMsal();
  const theme = useTheme();
  const navigate = useNavigate();
  
  const handleLogin = React.useCallback(() => {
    instance.loginRedirect(loginRequest);
  }, [instance]);

  const handleLogout = React.useCallback(() => {
    instance.logoutRedirect();
  }, [instance]);
  const publicUrl = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  const backgroundUrl = `${publicUrl}/images/View%20from%20oyster%20punt%20Merimbula.jpg`;
  const hasSession = accounts && accounts.length > 0;
  const [checkingSession, setCheckingSession] = React.useState(!hasSession);

  // If MSAL already has an account, skip the landing screen.
  React.useEffect(() => {
    const preferred = getPreferredAccount();
    const activeAccount = preferred || accounts?.[0];
    if (activeAccount) {
      instance.setActiveAccount(activeAccount);
      navigate("/", { replace: true });
      setCheckingSession(false);
    }
  }, [accounts, instance, navigate]);

  // Attempt silent SSO when the browser still has an IdP session but MSAL cache is empty.
  React.useEffect(() => {
    if (accounts?.length) {
      setCheckingSession(false);
      return; // already have an account cached
    }

    let cancelled = false;
    setCheckingSession(true);
    const trySilent = async () => {
      try {
        const result = await instance.ssoSilent(loginRequest);
        if (cancelled) return;
        if (result?.account) {
          instance.setActiveAccount(result.account);
          navigate("/", { replace: true });
        }
      } catch (err) {
        if (err instanceof InteractionRequiredAuthError) {
          // No cached session; user will need to click Continue.
          return;
        }
        // Non-interaction errors are logged for troubleshooting but do not block UI.
        console.warn("Silent SSO failed", err);
      } finally {
        if (!cancelled) {
          setCheckingSession(false);
        }
      }
    };

    trySilent();
    return () => {
      cancelled = true;
    };
  }, [accounts, instance, navigate]);
  return (
    <Box
      sx={{
        minHeight: "100vh",
        minWidth: "100vw",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundImage: `url(${backgroundUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <Container maxWidth="sm" sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        {checkingSession ? (
          <Stack alignItems="center" spacing={2} sx={{ p: 4, bgcolor: "rgba(15,22,34,0.7)", borderRadius: 2 }}>
            <CircularProgress color="primary" />
            <Typography variant="body2" color="#dfe6f3">
              Checking your session...
            </Typography>
          </Stack>
        ) : (
          <Paper elevation={12} sx={{ p: 4, width: "100%", background: "rgba(15,22,34,0.7)" }}>
            <Stack spacing={2}>
              <Typography variant="h4" fontWeight={700} color={theme.palette.primary.main}>
                OysterMate
              </Typography>
              
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button variant="contained"  onClick={handleLogin} sx={{ flex: 1, backgroundColor:theme.palette.primary.main }}>
                  Continue
                </Button>
                {hasSession && (
                  <Button variant="outlined" color="inherit" onClick={handleLogout} sx={{ flex: 1 }}>
                    Sign out
                  </Button>
                )}
              </Stack>
              <Typography variant="caption" color="#7b8597">
                Need password help? Use the "Forgot your password" link on the sign in screen to trigger the reset policy.
              </Typography>
            </Stack>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default AuthLanding;
